import { useTranslation } from 'react-i18next'

import type { Priority, StateCategory } from '@/types/domain'

/**
 * The small coloured pieces a dense interface is built from.
 *
 * Status is a filled pill rather than a word: on a list of forty rows, colour is
 * what lets someone find the blocked one without reading. The colour comes from
 * the normalised category, never from the column's name, so five divisions with
 * five different lifecycles still read the same way at a glance.
 */
const CATEGORY_COLOR: Record<StateCategory, string> = {
  backlog: 'var(--color-state-backlog)',
  started: 'var(--color-state-started)',
  blocked: 'var(--color-state-blocked)',
  done: 'var(--color-state-done)',
  cancelled: 'var(--color-state-cancelled)',
}

export function StatusPill({
  name,
  category,
  compact = false,
}: {
  name: string | null
  category: StateCategory | null
  compact?: boolean
}) {
  const { t } = useTranslation()
  const color = CATEGORY_COLOR[category ?? 'backlog']

  if (name === null) {
    return <span className="text-ink-500 font-mono text-[11px]">—</span>
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${
        compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'
      }`}
      style={{ backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)`, color }}
      title={category === null ? undefined : t(`state.${category}`)}
    >
      <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
      {name}
    </span>
  )
}

const PRIORITY_COLOR: Record<Priority, string> = {
  urgent: 'var(--color-state-blocked)',
  high: '#d97706',
  normal: 'var(--color-brand-500)',
  low: 'var(--color-ink-500)',
}

/** ClickUp's flag, because a colour in the same column every time is scannable. */
export function PriorityFlag({ priority }: { priority: Priority }) {
  const { t } = useTranslation()

  return (
    <span
      className="inline-flex items-center gap-1 text-[11px]"
      style={{ color: PRIORITY_COLOR[priority] }}
      title={t(`priority.${priority}`)}
    >
      <svg viewBox="0 0 24 24" className="size-3.5" aria-hidden="true">
        <path
          d="M5 21V4h11l-2 3.5L16 11H5"
          fill={priority === 'low' ? 'none' : 'currentColor'}
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinejoin="round"
        />
      </svg>
      <span className="sr-only">{t(`priority.${priority}`)}</span>
    </span>
  )
}

/**
 * Initials on a coloured disc.
 *
 * The colour is derived from the name rather than stored, so a person looks the
 * same everywhere without a migration — and two people with the same initials
 * are still told apart.
 */
export function Avatar({
  name,
  color,
  size = 'md',
}: {
  name: string | null
  color?: string | null | undefined
  size?: 'sm' | 'md'
}) {
  const { t } = useTranslation()

  if (name === null) {
    return (
      <span
        title={t('work.unassigned')}
        className={`border-line text-ink-500 inline-flex items-center justify-center rounded-full border border-dashed ${
          size === 'sm' ? 'size-5 text-[9px]' : 'size-6 text-[10px]'
        }`}
      >
        ?
      </span>
    )
  }

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase()

  return (
    <span
      title={name}
      className={`inline-flex items-center justify-center rounded-full font-medium text-white ${
        size === 'sm' ? 'size-5 text-[9px]' : 'size-6 text-[10px]'
      }`}
      style={{ backgroundColor: color ?? hueFor(name) }}
    >
      {initials}
    </span>
  )
}

function hueFor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 360

  return `hsl(${hash} 52% 45%)`
}

/** A date that turns red once it is behind us. */
export function DueDate({ date, asOf }: { date: string | null; asOf: number }) {
  const { t } = useTranslation()

  if (date === null) return <span className="text-ink-500 text-[11px]">—</span>

  const overdue = asOf !== 0 && Date.parse(date) < asOf

  return (
    <span
      className={`font-mono text-[11px] tabular-nums ${
        overdue ? 'font-semibold text-[color:var(--color-state-blocked)]' : 'text-ink-700'
      }`}
      title={overdue ? t('work.overdue') : undefined}
    >
      {date}
    </span>
  )
}
